
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import QRCode from './QRCode';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Merchant, Location } from '../types';
import VideoPlayer from '@/components/profile/VideoPlayer';
import { MapPin, Globe, Phone, Mail, Video } from 'lucide-react';
import { checkUserAtMerchantLocation } from '@/services/merchantService';

function MerchantProfilePage() {
  const { id } = useParams<{ id: string }>();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [recordings, setRecordings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCurrentUserAtLocation, setIsCurrentUserAtLocation] = useState(false);
  const [userLocation, setUserLocation] = useState<Location | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isMerchantOwner, setIsMerchantOwner] = useState(false);
  
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setCurrentUserId(data.session.user.id);
      }
    };
    
    checkAuth();
  }, []);
  
  useEffect(() => {
    if (id) {
      fetchMerchantProfile(id);
      fetchMerchantRecordings(id);
      checkUserLocation();
    }
  }, [id, currentUserId]);
  
  const fetchMerchantProfile = async (merchantId: string) => {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .eq('id', merchantId)
        .single();
      
      if (error) {
        throw error;
      }
      
      if (data) {
        console.log("Merchant profile data:", data);
        setMerchant(data);
        
        // Check if current user is the merchant owner
        if (currentUserId && data.merchant_id === currentUserId) {
          setIsMerchantOwner(true);
        }
      }
    } catch (error) {
      console.error('Error fetching merchant profile:', error);
      toast.error('Failed to load merchant profile');
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchMerchantRecordings = async (merchantId: string) => {
    try {
      const { data, error } = await supabase
        .from('merchant_videos')
        .select(`
          recording_id,
          recordings (*)
        `)
        .eq('merchant_id', merchantId);
      
      if (error) throw error;
      
      if (data) {
        console.log("Merchant recordings data:", data);
        const recordingsData = data.map(item => item.recordings);
        setRecordings(recordingsData.filter(Boolean));
      }
    } catch (error) {
      console.error('Error fetching merchant recordings:', error);
    }
  };
  
  const checkUserLocation = async () => {
    if (!id || !currentUserId) return;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async position => {
          const userCoords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setUserLocation(userCoords);
          
          // Use the merchant service to check if user is at location
          const isAtLocation = await checkUserAtMerchantLocation(
            id,
            userCoords.latitude,
            userCoords.longitude
          );
          
          setIsCurrentUserAtLocation(isAtLocation);
        },
        error => {
          console.error('Error getting user location:', error);
          // For development purposes, allow recording without location
          setIsCurrentUserAtLocation(true);
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser');
      // For development purposes, allow recording without location
      setIsCurrentUserAtLocation(true);
    }
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto p-4 flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
      </div>
    );
  }
  
  if (!merchant) {
    return (
      <div className="container mx-auto p-4 pt-20">
        <h1 className="text-2xl text-white">Merchant not found</h1>
        <Button asChild className="mt-4">
          <Link to="/merchants">Back to Merchants</Link>
        </Button>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4 pt-20">
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/3 mb-4 md:mb-0 flex justify-center">
              {merchant.logo_path ? (
                <img 
                  src={`${merchant.logo_path}`}
                  alt={`${merchant.business_name} logo`}
                  className="w-48 h-48 object-contain rounded-lg"
                />
              ) : (
                <div className="w-48 h-48 bg-gray-700 rounded-lg flex items-center justify-center">
                  <span className="text-gray-400">No Logo</span>
                </div>
              )}
            </div>
            
            <div className="md:w-2/3 md:pl-6">
              <h1 className="text-3xl font-bold text-white">{merchant.business_name}</h1>
              
              {merchant.description && (
                <p className="text-gray-300 mt-2">{merchant.description}</p>
              )}
              
              <div className="mt-4 space-y-2">
                {merchant.address && (
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-yellow-600 mr-2" />
                    <span className="text-gray-300">{merchant.address}</span>
                  </div>
                )}
                
                {merchant.website_url && (
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 text-yellow-600 mr-2" />
                    <a href={merchant.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      {merchant.website_url}
                    </a>
                  </div>
                )}
              </div>
              
              <div className="mt-6">
                <h3 className="text-xl text-white">Share this merchant:</h3>
                <div className="mt-2">
                  <QRCode 
                    value={`${window.location.origin}/merchant/${id}`} 
                    size={150}
                    backgroundColor="#1E293B"
                    foregroundColor="#FFFFFF"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex flex-wrap gap-2">
                {(isCurrentUserAtLocation || isMerchantOwner) && (
                  <Button asChild className="bg-yellow-600 hover:bg-yellow-700 text-black">
                    <Link to={`/merchant/${id}/record`}>
                      <Video className="h-4 w-4 mr-2" />
                      Record Video
                    </Link>
                  </Button>
                )}
                
                {isMerchantOwner && (
                  <Button asChild variant="outline">
                    <Link to="/profile?tab=merchant">
                      Edit Merchant Profile
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="mt-8">
        <h2 className="text-2xl font-bold text-white mb-4">Recent Videos</h2>
        
        {recordings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recordings.map((recording: any) => (
              <VideoPlayer 
                key={recording.id}
                id={recording.id}
                title={recording.title}
                video_path={recording.video_path}
                avatar_path={recording.avatar_path}
                emotion={recording.emotion}
                location={recording.location}
                created_at={recording.created_at}
                user_id={recording.user_id}
                merchant_id={merchant.id}
              />
            ))}
          </div>
        ) : (
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <p className="text-gray-300">No videos recorded at this location yet.</p>
            {(isCurrentUserAtLocation || isMerchantOwner) && (
              <Button asChild className="mt-4 bg-yellow-600 hover:bg-yellow-700 text-black">
                <Link to={`/merchant/${id}/record`}>
                  Be the first to record a video here!
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MerchantProfilePage;
